/* eslint-disable indent */
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import Table from 'react-bootstrap/Table'
import Button from 'react-bootstrap/Button'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import Spinner from 'react-bootstrap/Spinner'
import Card from 'react-bootstrap/Card'
import Alert from 'react-bootstrap/Alert'
import Form from 'react-bootstrap/Form'
import targetLevelsService from '../services/targetLevels'
import { getTranslation } from '../services/translations'
import { translations } from '../services/arrays'
import { BackToListsButton } from '../services/helpfunctions'

const skillColumns = [
  { key: 'keeperSkill', label: 'keeperSkill' },
  { key: 'defenderSkill', label: 'defenderSkill' },
  { key: 'playmakerSkill', label: 'playmakerSkill' },
  { key: 'wingerSkill', label: 'wingerSkill' },
  { key: 'passingSkill', label: 'passingSkill' },
  { key: 'scorerSkill', label: 'scorerSkill' },
  { key: 'setPiecesSkill', label: 'setPiecesSkill' },
]

const TargetLevels = () => {
  const [targetLevels, setTargetLevels] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('U21')
  const [editMode, setEditMode] = useState(false)
  const [editData, setEditData] = useState(null)
  const [message, setMessage] = useState(null)
  const [messageType, setMessageType] = useState('success')
  const navigate = useNavigate()
  const user = useSelector(state => state.user)
  const lang = (user && user.language) || 'fi'
  const t = key => getTranslation(lang, key)
  const language = lang === 'en' ? 1 : 0

  const hasStaffAccess = user && user.staff && user.staff.length > 0
  const isHeadScout =
    user && user.staff && user.staff.some(s => s.role === 'HeadScout')

  // Hae nykyinen kausi nation-tiedoista (käytetään paria/parittomuutta)
  // Kausi 93 on pariton → MM, kausi 92 on parillinen → EM
  const currentSeason = 93 // Oletuskausi, voidaan hakea nations-kannasta tarvittaessa

  useEffect(() => {
    if (user && user.staff !== undefined && !hasStaffAccess) {
      navigate('/')
    }
  }, [user, hasStaffAccess, navigate])

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const loggedUser = window.localStorage.getItem('loggedUser')
        const token = loggedUser ? JSON.parse(loggedUser) : null

        if (token) {
          const data = await targetLevelsService.getAll(token)
          setTargetLevels(data)
        }
      } catch (error) {
        console.error('Error fetching target levels:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const showMessage = (msg, type = 'success') => {
    setMessage(msg)
    setMessageType(type)
    setTimeout(() => setMessage(null), 3000)
  }

  const handleSeed = async () => {
    try {
      const loggedUser = window.localStorage.getItem('loggedUser')
      const token = loggedUser ? JSON.parse(loggedUser) : null

      if (token) {
        const data = await targetLevelsService.seed(token)
        setTargetLevels(data)
        showMessage(t('targetLevelsInitialized'))
      }
    } catch (error) {
      console.error('Error seeding target levels:', error)
      showMessage(t('targetLevelsSaveFailed'), 'danger')
    }
  }

  const startEdit = targetLevel => {
    setEditData({
      ...targetLevel,
      skills: targetLevel.skills.map(s => ({ ...s })),
    })
    setEditMode(true)
  }

  const cancelEdit = () => {
    setEditMode(false)
    setEditData(null)
  }

  const handleSkillChange = (playerTypeIndex, skillKey, value) => {
    const newSkills = [...editData.skills]
    const parsedValue =
      value === '' || value === '-' ? null : parseFloat(value.replace(',', '.'))
    newSkills[playerTypeIndex] = {
      ...newSkills[playerTypeIndex],
      [skillKey]: parsedValue,
    }
    setEditData({ ...editData, skills: newSkills })
  }

  const handleSave = async () => {
    try {
      const loggedUser = window.localStorage.getItem('loggedUser')
      const token = loggedUser ? JSON.parse(loggedUser) : null

      if (token && editData) {
        const updated = await targetLevelsService.update(token, editData.id, {
          skills: editData.skills,
          description: editData.description,
        })
        setTargetLevels(
          targetLevels.map(tl => (tl.id === updated.id ? updated : tl))
        )
        setEditMode(false)
        setEditData(null)
        showMessage(t('targetLevelsSaved'))
      }
    } catch (error) {
      console.error('Error saving target levels:', error)
      showMessage(t('targetLevelsSaveFailed'), 'danger')
    }
  }

  const getCompetitionLabel = competitionType => {
    switch (competitionType) {
      case 'EM':
        return t('emPlayers')
      case 'MM':
        return t('mmPlayers')
      case 'senior':
        return t('seniorPlayers')
      default:
        return competitionType
    }
  }

  const getSeasonInfo = () => {
    const isOddSeason = currentSeason % 2 !== 0
    return {
      season: currentSeason,
      isOddSeason,
      activeCompetition: isOddSeason ? 'MM' : 'EM',
    }
  }

  const renderTargetTable = (targetLevel, isEditing) => {
    const data = isEditing ? editData : targetLevel

    return (
      <div key={data.id} className="mb-4">
        <h5 className="text-light">
          {getCompetitionLabel(data.competitionType)}
          {data.description && (
            <small className="text-muted ms-2">({data.description})</small>
          )}
        </h5>
        <Table striped bordered variant="dark" size="sm" responsive>
          <thead>
            <tr>
              <th>{t('playerType')}</th>
              {skillColumns.map(col => (
                <th key={col.key} className="text-center">
                  {translations[col.key]
                    ? translations[col.key][language]
                    : col.key}
                </th>
              ))}
              {isEditing && isHeadScout && <th></th>}
            </tr>
          </thead>
          <tbody>
            {data.skills.map((skill, index) => (
              <tr key={skill.playerType}>
                <td>
                  <strong>{skill.playerType}</strong>
                </td>
                {skillColumns.map(col => (
                  <td key={col.key} className="text-center">
                    {isEditing ? (
                      <Form.Control
                        type="text"
                        size="sm"
                        value={
                          skill[col.key] === null ||
                          skill[col.key] === undefined
                            ? ''
                            : String(skill[col.key]).replace('.', ',')
                        }
                        onChange={e =>
                          handleSkillChange(index, col.key, e.target.value)
                        }
                        style={{
                          width: '60px',
                          textAlign: 'center',
                          backgroundColor: skill[col.key]
                            ? '#2a2d31'
                            : '#1a1a1a',
                          color: '#fff',
                          border: '1px solid #555',
                        }}
                        placeholder="-"
                      />
                    ) : (
                      <span>
                        {skill[col.key] !== null && skill[col.key] !== undefined
                          ? String(skill[col.key]).replace('.', ',')
                          : '-'}
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
        {isEditing && (
          <div className="d-flex gap-2">
            <Button variant="success" onClick={handleSave}>
              {t('saveChanges')}
            </Button>
            <Button variant="secondary" onClick={cancelEdit}>
              {t('cancelEdit')}
            </Button>
          </div>
        )}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="text-center mt-4">
        <Spinner animation="border" variant="light" />
        <p className="text-light mt-2">{t('loadingTargetLevels')}</p>
      </div>
    )
  }

  if (user && user.staff !== undefined && !hasStaffAccess) {
    return (
      <Card bg="dark" text="light" className="mt-3">
        <Card.Body className="text-center">
          <h4>{t('accessDenied')}</h4>
          <p>{t('staffOnlyPage')}</p>
          <Button variant="primary" onClick={() => navigate('/')}>
            {t('returnToHome')}
          </Button>
        </Card.Body>
      </Card>
    )
  }

  const seasonInfo = getSeasonInfo()
  const u21Levels = targetLevels.filter(tl => tl.teamType === 'U21')
  const seniorLevels = targetLevels.filter(tl => tl.teamType === 'senior')

  // Järjestä U21: aktiivinen kilpailu ensin
  const sortedU21 = [...u21Levels].sort((a, b) => {
    if (a.competitionType === seasonInfo.activeCompetition) return -1
    if (b.competitionType === seasonInfo.activeCompetition) return 1
    return 0
  })

  return (
    <Card bg="dark" text="light" className="mt-3">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <BackToListsButton />
          <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
            {t('targetLevelsTitle')}
          </span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <small className="text-muted">
            {t('currentSeason')}: {currentSeason} (
            {seasonInfo.isOddSeason ? t('oddSeason') : t('evenSeason')})
          </small>
        </div>
      </Card.Header>
      <Card.Body>
        {message && (
          <Alert
            variant={messageType}
            dismissible
            onClose={() => setMessage(null)}
          >
            {message}
          </Alert>
        )}

        {targetLevels.length === 0 ? (
          <div className="text-center">
            <p>{t('noTargetLevels')}</p>
            {isHeadScout && (
              <Button variant="primary" onClick={handleSeed}>
                {t('initializeDefaults')}
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="mb-3">
              <ButtonGroup>
                <Button
                  variant={activeTab === 'U21' ? 'info' : 'secondary'}
                  onClick={() => setActiveTab('U21')}
                >
                  {t('u21Team')} ({u21Levels.length})
                </Button>
                <Button
                  variant={activeTab === 'senior' ? 'info' : 'secondary'}
                  onClick={() => setActiveTab('senior')}
                >
                  {t('seniorTeam')} ({seniorLevels.length})
                </Button>
              </ButtonGroup>
            </div>

            {activeTab === 'U21' && (
              <>
                {sortedU21.map(tl => {
                  const isEditing =
                    editMode && editData && editData.id === tl.id
                  return (
                    <div key={tl.id}>
                      {!isEditing && isHeadScout && !editMode && (
                        <Button
                          variant="outline-warning"
                          size="sm"
                          className="mb-2"
                          onClick={() => startEdit(tl)}
                        >
                          {t('editTargetLevels')}
                        </Button>
                      )}
                      {tl.competitionType === seasonInfo.activeCompetition && (
                        <span className="badge bg-success ms-2 mb-2">
                          ● {t('currentSeason')}
                        </span>
                      )}
                      {!isHeadScout && !editMode && (
                        <small className="text-muted ms-2">
                          {t('headScoutOnly')}
                        </small>
                      )}
                      {renderTargetTable(tl, isEditing)}
                    </div>
                  )
                })}
              </>
            )}

            {activeTab === 'senior' && (
              <>
                {seniorLevels.map(tl => {
                  const isEditing =
                    editMode && editData && editData.id === tl.id
                  return (
                    <div key={tl.id}>
                      {!isEditing && isHeadScout && !editMode && (
                        <Button
                          variant="outline-warning"
                          size="sm"
                          className="mb-2"
                          onClick={() => startEdit(tl)}
                        >
                          {t('editTargetLevels')}
                        </Button>
                      )}
                      {!isHeadScout && !editMode && (
                        <small className="text-muted ms-2">
                          {t('headScoutOnly')}
                        </small>
                      )}
                      {renderTargetTable(tl, isEditing)}
                    </div>
                  )
                })}
              </>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  )
}

export default TargetLevels
